// Grammar Book Metadata
// Contains metadata about the grammar book system
// Organized for Supabase migration and systematic data management

export const grammarBookMetadata = {
  // System information
  systemInfo: {
    name: "GrammarSpeak Grammar Books",
    version: "1.0.0",
    description: "Comprehensive English grammar learning system with systematic progression",
    author: "GrammarSpeak Team",
    lastUpdated: new Date().toISOString(),
    totalUnits: 18,
    completedUnits: 3,
    inProgressUnits: 0,
    notStartedUnits: 15
  },

  // Difficulty levels
  difficultyLevels: {
    beginner: {
      name: { traditional: '初級', simplified: '初级', english: 'Beginner' },
      description: { traditional: '適合英語初學者', simplified: '适合英语初学者', english: 'Suitable for English beginners' },
      color: 'from-green-500 to-emerald-600',
      units: [1, 2]
    },
    intermediate: {
      name: { traditional: '中級', simplified: '中级', english: 'Intermediate' },
      description: { traditional: '適合有一定英語基礎的學習者', simplified: '适合有一定英语基础的学习者', english: 'Suitable for learners with basic English foundation' },
      color: 'from-blue-500 to-cyan-600',
      units: [3, 4, 7]
    },
    advanced: {
      name: { traditional: '高級', simplified: '高级', english: 'Advanced' },
      description: { traditional: '適合英語程度較高的學習者', simplified: '适合英语程度较高的学习者', english: 'Suitable for advanced English learners' },
      color: 'from-purple-500 to-violet-600',
      units: [5, 6, 8]
    }
  },

  // Learning progression
  learningProgression: {
    phase1: {
      name: "基礎語法階段",
      description: "掌握基本句型結構和時態",
      units: [1, 2],
      estimatedHours: 5
    },
    phase2: {
      name: "進階語法階段",
      description: "學習被動語態和助動詞",
      units: [3, 4],
      estimatedHours: 5.5
    },
    phase3: {
      name: "高級語法階段",
      description: "掌握情態助動詞和假設語氣",
      units: [5, 6],
      estimatedHours: 7.5
    },
    phase4: {
      name: "專業語法階段",
      description: "學習主詞動詞一致性和不定詞",
      units: [7, 8],
      estimatedHours: 7.5
    }
  },

  // Features and capabilities
  features: {
    multiLanguage: {
      supported: true,
      languages: ['traditional', 'simplified', 'english'],
      defaultLanguage: 'traditional'
    },
    progressTracking: {
      enabled: true,
      features: ['completion', 'timeSpent', 'scores', 'attempts']
    },
    interactiveLearning: {
      enabled: true,
      modes: ['read', 'study', 'test'],
      features: ['examples', 'exercises', 'quizzes']
    },
    exportImport: {
      enabled: true,
      formats: ['json'],
      features: ['progress', 'content', 'settings']
    }
  },

  // Content structure
  contentStructure: {
    unitTypes: {
      basicPatterns: {
        description: "Basic sentence patterns and structures",
        units: [1]
      },
      tenseAspect: {
        description: "Verb tenses and aspect",
        units: [2]
      },
      voice: {
        description: "Active and passive voice",
        units: [3]
      },
      auxiliaries: {
        description: "Auxiliary and modal verbs",
        units: [4, 5]
      },
      conditionals: {
        description: "Conditional sentences and subjunctive",
        units: [6]
      },
      agreement: {
        description: "Subject-verb agreement",
        units: [7]
      },
      nonFinites: {
        description: "Infinitives, gerunds, and participles",
        units: [8]
      }
    }
  },

  // Assessment and evaluation
  assessment: {
    quizTypes: {
      multipleChoice: {
        enabled: true,
        description: "Multiple choice questions"
      },
      fillInBlanks: {
        enabled: true,
        description: "Fill in the blanks exercises"
      },
      sentenceConstruction: {
        enabled: true,
        description: "Sentence construction exercises"
      },
      patternRecognition: {
        enabled: true,
        description: "Grammar pattern recognition"
      }
    },
    scoring: {
      passingScore: 70,
      excellentScore: 90,
      maxAttempts: 3
    }
  },

  // Technical specifications
  technical: {
    dataFormat: "JSON",
    encoding: "UTF-8",
    versioning: "Semantic versioning",
    compatibility: {
      minAppVersion: "1.0.0",
      supportedBrowsers: ["Chrome", "Firefox", "Safari", "Edge"]
    }
  }
};

// Helper functions
export const getDifficultyLevel = (unitId) => {
  for (const [level, data] of Object.entries(grammarBookMetadata.difficultyLevels)) {
    if (data.units.includes(unitId)) {
      return level;
    }
  }
  return 'intermediate';
};

export const getPhase = (unitId) => {
  for (const [phase, data] of Object.entries(grammarBookMetadata.learningProgression)) {
    if (data.units.includes(unitId)) {
      return phase;
    }
  }
  return 'phase1';
};

export const getUnitType = (unitId) => {
  for (const [type, data] of Object.entries(grammarBookMetadata.contentStructure.unitTypes)) {
    if (data.units.includes(unitId)) {
      return type;
    }
  }
  return 'basicPatterns';
};

// Default export
export default grammarBookMetadata;
